import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Badge } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { Toggle } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Table } from '@heartinternet/hi-ui';
import type { Column } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { ConfirmationDialog } from '@heartinternet/hi-ui';
import { Modal } from '@heartinternet/hi-ui';
import type { ManagePackagesPageProps, Package } from './types';
import { AddToGroupModal } from './components/AddToGroupModal';
import { RenameGroupModal } from './components/RenameGroupModal';
import { MigrateModal } from './components/MigrateModal';
import { ManagePackageMenu } from './components/ManagePackageMenu';
import './ManagePackagesPage.css';

export type { ManagePackagesPageProps, Package };

const PACKAGES_ENDPOINT = '/manage/manage-packages.cgi?json=1';
const GROUPS_ENDPOINT = '/manage/package-groups.cgi';
const UPDATE_ENDPOINT = '/manage/manage-packages-w.cgi/update';
const DELETE_ENDPOINT = '/manage/manage-packages-w.cgi/delete';
const GROUPS_UPDATE_ENDPOINT = '/manage/package-groups-update.cgi';

// The CGI sends `date_added_s` as a zero-padded GB date (DD/MM/YYYY), which is
// not lexically sortable. Turn it into a YYYYMMDD integer so the Created column
// sorts by actual date rather than day-of-month. Malformed input sorts first.
export const toDateSortKey = (gbDate: string): number => {
  const [day, month, year] = (gbDate || '').split('/');
  return Number(`${year}${month}${day}`) || 0;
};

export const ManagePackagesPage: React.FC<ManagePackagesPageProps> = ({
  permissions,
  hasPremiumHosting,
  migrationShared2PremiumEnabled,
  snapshotsEnabled,
  noExtendRelogin,
}) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [groupFilter, setGroupFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // Sorting & pagination
  const [sortKey, setSortKey] = useState<string>('login');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Selection
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set());

  // Modals
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [removeFromGroupConfirmOpen, setRemoveFromGroupConfirmOpen] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState<{ open: boolean; pkg: Package | null }>({
    open: false,
    pkg: null,
  });
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [renameGroupOpen, setRenameGroupOpen] = useState(false);
  const [deleteGroupConfirmOpen, setDeleteGroupConfirmOpen] = useState(false);
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);

  const fetchData = useCallback(async (group?: string) => {
    setLoading(true);
    setError(null);

    try {
      const pkgUrl = group
        ? `/manage/manage-packages.cgi/${encodeURIComponent(group)}?json=1`
        : PACKAGES_ENDPOINT;

      const [pkgResponse, grpResponse] = await Promise.all([fetch(pkgUrl), fetch(GROUPS_ENDPOINT)]);

      const pkgData = await pkgResponse.json();
      const grpData = await grpResponse.json();

      if (!pkgData.accessAllowed) {
        setError('You do not have permission to view hosting packages.');
        setLoading(false);
        return;
      }

      /* v8 ignore next 2 -- defensive fallback for malformed API response */
      setPackages(pkgData.packageList || []);
      setGroups(grpData || []);
    } catch {
      setError('Failed to load hosting packages. Please refresh the page.');
    }

    setLoading(false);
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect -- data fetching effect legitimately sets loading state */
  useEffect(() => {
    void fetchData();
  }, [fetchData]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Filtered data (search is client-side, group filter is server-side via refetch)
  const filteredPackages = useMemo(() => {
    if (!searchFilter) return packages;

    try {
      const regex = new RegExp(searchFilter, 'i');
      /* v8 ignore start -- defensive fallback for malformed API response */
      return packages.filter((p) => regex.test(p.short_domain || '') || regex.test(p.login || ''));
    } catch {
      const lower = searchFilter.toLowerCase();
      return packages.filter(
        (p) =>
          (p.short_domain || '').toLowerCase().includes(lower) ||
          (p.login || '').toLowerCase().includes(lower)
      );
    }
    /* v8 ignore stop */
  }, [packages, searchFilter]);

  // Sorted data
  const sortedPackages = useMemo(() => {
    return [...filteredPackages].sort((a, b) => {
      if (sortKey === 'date_added_s') {
        const cmp = toDateSortKey(a.date_added_s) - toDateSortKey(b.date_added_s);
        return sortDirection === 'asc' ? cmp : -cmp;
      }

      let aVal = '';
      let bVal = '';

      /* v8 ignore start -- defensive fallbacks for malformed API response / migration ternaries */
      switch (sortKey) {
        case 'login':
          aVal = (a.short_domain || '').toLowerCase();
          bVal = (b.short_domain || '').toLowerCase();
          break;
        case 'resold_type_name': {
          const aMigrating = a.migration?.status === 'started';
          const bMigrating = b.migration?.status === 'started';
          aVal = aMigrating ? '' : (a.resold_type_name || '').toLowerCase();
          bVal = bMigrating ? '' : (b.resold_type_name || '').toLowerCase();
          break;
        }
        case 'active':
          aVal = a.migration?.status === 'started' ? '' : String(a.active);
          bVal = b.migration?.status === 'started' ? '' : String(b.active);
          break;
      }
      /* v8 ignore stop */

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredPackages, sortKey, sortDirection]);

  // Paginated data
  const paginatedPackages = useMemo(
    () => sortedPackages.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedPackages, currentPage, pageSize]
  );

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Toggle enable/disable
  const buildUpdateFormData = useCallback(
    (togglePkg: Package, newStatus: '0' | '1') => {
      const formData = new URLSearchParams();
      packages.forEach((p) => {
        formData.append('id', p.oid);
        formData.append('status', p.oid === togglePkg.oid ? newStatus : String(p.active));
      });
      return formData;
    },
    [packages]
  );

  const handleToggle = useCallback(
    async (pkg: Package) => {
      if (Number(pkg.active) === 1) {
        setDisableConfirm({ open: true, pkg });
      } else {
        try {
          await fetch(UPDATE_ENDPOINT, { method: 'POST', body: buildUpdateFormData(pkg, '1') });
          await fetchData();
        } catch {
          setError('Failed to enable package.');
        }
      }
    },
    [fetchData, buildUpdateFormData]
  );

  const confirmDisable = useCallback(async () => {
    const pkg = disableConfirm.pkg!;
    setDisableConfirm({ open: false, pkg: null });
    try {
      await fetch(UPDATE_ENDPOINT, {
        method: 'POST',
        body: buildUpdateFormData(pkg, '0'),
      });
      await fetchData();
    } catch {
      setError('Failed to disable package.');
    }
  }, [disableConfirm.pkg, fetchData, buildUpdateFormData]);

  const selectedPackages = useMemo(
    () => packages.filter((p) => selectedRows.has(p.oid)),
    [packages, selectedRows]
  );

  // Delete selected
  const handleDelete = useCallback(async () => {
    const rows = new Set(selectedRows);
    setDeleteConfirmOpen(false);
    try {
      const formData = new URLSearchParams();
      rows.forEach((oid) => {
        formData.append('delete', String(oid));
      });
      await fetch(DELETE_ENDPOINT, { method: 'POST', body: formData });
      setSelectedRows(new Set());
      await fetchData();
    } catch {
      setError('Failed to delete packages.');
    }
  }, [selectedRows, fetchData]);

  // Remove selected from group
  const handleRemoveFromGroup = useCallback(async () => {
    const pkgs = [...selectedPackages];
    setRemoveFromGroupConfirmOpen(false);
    try {
      const formData = new URLSearchParams();
      pkgs.forEach((p) => {
        formData.append('remove-member-all', p.login);
      });
      await fetch(GROUPS_UPDATE_ENDPOINT, { method: 'POST', body: formData });
      await fetchData();
    } catch {
      setError('Failed to remove packages from group.');
    }
  }, [selectedPackages, fetchData]);

  const handleRenameGroup = useCallback(
    async (newName: string) => {
      const sanitised = newName.replace(/[/\\&#?<>]+/g, ' ').trim();
      /* v8 ignore start -- sanitisation guard; RenameGroupModal validates before calling */
      if (!sanitised || sanitised === groupFilter) return;
      /* v8 ignore stop */

      setRenameGroupOpen(false);
      try {
        const formData = new URLSearchParams();
        formData.append('rename-group', JSON.stringify({ from: groupFilter, to: sanitised }));
        await fetch(GROUPS_UPDATE_ENDPOINT, { method: 'POST', body: formData });
        setGroupFilter(sanitised);
        await fetchData(sanitised);
      } catch {
        setError('Failed to rename group.');
      }
    },
    [groupFilter, fetchData]
  );

  const handleDeleteGroup = useCallback(async () => {
    const group = groupFilter;
    setDeleteGroupConfirmOpen(false);
    try {
      const formData = new URLSearchParams();
      formData.append('delete-group', group);
      await fetch(GROUPS_UPDATE_ENDPOINT, { method: 'POST', body: formData });
      setGroupFilter('');
      await fetchData();
    } catch {
      setError('Failed to delete group.');
    }
  }, [groupFilter, fetchData]);

  const isMigrating = (pkg: Package) => pkg.migration?.status === 'started';

  const columns: Column<Package>[] = useMemo(
    () => [
      {
        key: 'date_added_s',
        label: 'Created',
        sortable: true,
        hideOnMobile: true,
        render: (value: unknown) => (
          <Text variant="body-small" inline>
            {value as string}
          </Text>
        ),
      },
      {
        key: 'login',
        label: 'Domain',
        sortable: true,
        render: (_value: unknown, row: Package) => {
          if (!row.short_domain) return null;
          return (
            <Text variant="body-small" inline>
              <Link href={`http://${row.login}`} external>
                {row.short_domain}
              </Link>
            </Text>
          );
        },
      },
      {
        key: 'resold_type_name',
        label: 'Package',
        sortable: true,
        render: (value: unknown, row: Package) => {
          if (isMigrating(row)) {
            return <Badge variant="info">Migrating...</Badge>;
          }
          const iconName =
            row.web_server_type === 'windows'
              ? 'windows'
              : Number(row.is_hemsx) === 1
                ? 'premium-hosting'
                : 'linux';
          return (
            <Flex
              direction="row"
              gap="xs"
              align="center"
              className="hi-ui-manage-packages__package-cell"
            >
              <Icon name={iconName} size={16} />
              <Text variant="body-small" inline>
                {value as string}
              </Text>
            </Flex>
          );
        },
      },
      {
        key: 'active',
        label: 'Enabled',
        sortable: true,
        render: (_value: unknown, row: Package) => {
          if (isMigrating(row)) return null;
          return (
            <Toggle
              checked={Number(row.active) === 1}
              onChange={() => handleToggle(row)}
              aria-label={`${Number(row.active) === 1 ? 'Disable' : 'Enable'} ${row.short_domain || row.login}`}
            />
          );
        },
      },
      {
        key: 'oid',
        label: '',
        align: 'right' as const,
        render: (_value: unknown, row: Package) => (
          <ManagePackageMenu
            row={row}
            permissions={permissions}
            noExtendRelogin={noExtendRelogin}
          />
        ),
      },
    ],
    [handleToggle, permissions, noExtendRelogin]
  );

  const hasSelection = selectedRows.size > 0;

  return (
    <div className="hi-ui-manage-packages-page">
      <Text variant="heading-1">Manage Hosting Accounts</Text>
      <Text variant="body">
        Your web hosting packages are shown below. You can enable, disable and delete packages, or
        use the cog menu on each row to access the control panel, edit the account and manage the
        domain.
      </Text>

      {error && (
        <Notice variant="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Notice>
      )}

      <Card variant="surface" padding="sm">
        <Flex direction="row" gap="sm" align="center" wrap>
          <Select
            value={groupFilter}
            onChange={(e) => {
              const group = e.target.value;
              setGroupFilter(group);
              setCurrentPage(1);
              /* v8 ignore start -- empty string to undefined conversion */
              void fetchData(group || undefined);
              /* v8 ignore stop */
            }}
            size="sm"
            aria-label="Filter by group"
          >
            <option value="">Filter by group</option>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
          <Button
            variant="primary"
            size="small"
            onClick={() => setAddToGroupOpen(true)}
            disabled={!hasSelection}
          >
            Group
          </Button>
          <Button
            variant="tertiary"
            size="small"
            onClick={() => setRemoveFromGroupConfirmOpen(true)}
            disabled={!hasSelection}
          >
            Ungroup
          </Button>
          {groupFilter && (
            <>
              <Button variant="tertiary" size="small" onClick={() => setRenameGroupOpen(true)}>
                Rename Group
              </Button>
              <Button
                variant="warning"
                size="small"
                onClick={() => setDeleteGroupConfirmOpen(true)}
              >
                Delete Group
              </Button>
            </>
          )}
        </Flex>
      </Card>

      <div className="hi-ui-manage-packages__search-links-row">
        <Input
          type="text"
          value={searchFilter}
          onChange={(e) => {
            setSearchFilter(e.target.value);
            setCurrentPage(1);
          }}
          size="sm"
          placeholder="Filter by domain"
          aria-label="Filter by domain"
        />
        <Flex direction="row" gap="md" className="hi-ui-manage-packages__page-links">
          {snapshotsEnabled && <Link href="/manage/reseller/backups.cgi">Snapshot Backups</Link>}
          <Link href="/manage/pal.cgi?count=100&sort-type=by_name&min=0" target="_blank">
            Printer Friendly Version
          </Link>
          <Link href="/manage/pal.cgi?csv=1&sort-type=by_name">Export to CSV</Link>
        </Flex>
      </div>

      {loading ? (
        <div className="hi-ui-manage-packages__loading">
          <Spinner size="medium" />
        </div>
      ) : (
        <>
          <Table<Package>
            columns={columns}
            data={paginatedPackages}
            selectable
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            getRowKey={(row: Package) => row.oid}
            isRowSelectable={(row: Package) => row.migration?.status !== 'started'}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            pagination={{
              currentPage,
              pageSize,
              totalItems: sortedPackages.length,
              pageSizeOptions: [25, 50, 100],
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyMessage="No hosting accounts found."
          />

          <Flex
            direction="row"
            align="center"
            gap="sm"
            className="hi-ui-manage-packages__bulk-actions"
          >
            {permissions.hosting_delete && (
              <Button
                variant="warning"
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={!hasSelection}
              >
                Delete Selected{hasSelection ? ` (${selectedRows.size})` : ''}
              </Button>
            )}
            {hasPremiumHosting && migrationShared2PremiumEnabled && (
              <Button
                variant="secondary"
                size="small"
                disabled={!hasSelection}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMigrateModalOpen(true);
                }}
              >
                Migrate to Premium Hosting
              </Button>
            )}
          </Flex>
        </>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Packages"
        confirmLabel="Delete"
        confirmVariant="warning"
      >
        <Text variant="body">
          Are you sure you want to delete {selectedRows.size} package
          {selectedRows.size > 1 ? 's' : ''}? This cannot be undone.
        </Text>
        <ul className="hi-ui-manage-packages__delete-list">
          {selectedPackages.map((p) => (
            <li key={p.oid}>{p.short_domain || p.login}</li>
          ))}
        </ul>
        <Notice variant="warning">
          Deleting a package will permanently remove all associated mailboxes and emails. Please
          ensure any important messages have been downloaded or forwarded before proceeding.
        </Notice>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={disableConfirm.open}
        onCancel={() => setDisableConfirm({ open: false, pkg: null })}
        onConfirm={confirmDisable}
        title="Disable Package"
        confirmLabel="Disable"
        confirmVariant="warning"
      >
        <Text variant="body">
          Are you sure you want to disable{' '}
          <strong>{disableConfirm.pkg?.short_domain || disableConfirm.pkg?.login}</strong>?
        </Text>
        <Text variant="body-small">
          Disabled packages will be automatically deleted after 50 days.
        </Text>
      </ConfirmationDialog>

      <Modal
        isOpen={removeFromGroupConfirmOpen}
        onClose={() => setRemoveFromGroupConfirmOpen(false)}
        title="Ungroup"
        size="small"
        footer={
          <Button variant="primary" size="small" onClick={handleRemoveFromGroup}>
            Ungroup
          </Button>
        }
      >
        <Text variant="body" inline>
          Remove {selectedPackages.length} package
          {selectedPackages.length > 1 ? 's' : ''} from their groups? The packages will not be
          deleted.
        </Text>
      </Modal>

      <AddToGroupModal
        isOpen={addToGroupOpen}
        onClose={() => setAddToGroupOpen(false)}
        selectedPackages={selectedPackages}
        groups={groups}
        onGroupChanged={fetchData}
      />

      <RenameGroupModal
        isOpen={renameGroupOpen}
        onClose={() => setRenameGroupOpen(false)}
        currentName={groupFilter}
        onRename={handleRenameGroup}
      />

      <ConfirmationDialog
        isOpen={deleteGroupConfirmOpen}
        onCancel={() => setDeleteGroupConfirmOpen(false)}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        confirmLabel="Delete"
        confirmVariant="warning"
      >
        <Text variant="body">
          Are you sure you want to delete the group <strong>{groupFilter}</strong>? Packages in this
          group will become ungrouped.
        </Text>
      </ConfirmationDialog>

      <MigrateModal
        isOpen={migrateModalOpen}
        onClose={() => setMigrateModalOpen(false)}
        selectedPackages={selectedPackages}
        onMigrationScheduled={() => {
          void fetchData(groupFilter || undefined);
        }}
      />
    </div>
  );
};
